import React from 'react'
import {useField} from 'formik'
import {Input} from '~/src/design-system/Primitives'
import {
  FieldHelper,
  FieldLabel,
  FieldWrapper,
  RequiredIndicator,
} from '~/src/design-system-shims/FieldParts'

// Phone formatting utility function (from Staffology)
export const formatPhoneNumber = (value: string): string => {
  // Check if it's an international number (starts with +)
  const isInternational = value.startsWith('+')

  if (isInternational) {
    // For international numbers, only remove non-numeric chars except + and spaces
    // Keep the format flexible: +XX XXX XXX XXXX
    const cleaned = value.replace(/[^\d+\s]/g, '')
    return cleaned
  }

  // For US numbers, remove all non-numeric characters and format as (XXX) XXX-XXXX
  const phoneNumber = value.replace(/[^\d]/g, '')

  if (phoneNumber.length < 4) {
    return phoneNumber
  }
  if (phoneNumber.length < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`
  }
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`
}

interface PhoneInputProps {
  name: string
  label?: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
  fullWidth?: boolean
  helperText?: string
  error?: boolean
}

export const PhoneInput: React.FC<PhoneInputProps> = ({
  name,
  label = '',
  placeholder = '(555) 123-4567',
  required = false,
  disabled = false,
  fullWidth = true,
}) => {
  const [field, meta, helpers] = useField(name)
  const hasError = !!(meta.touched && meta.error)
  const errorText = hasError && meta.error !== 'DoNotShow' ? meta.error : undefined

  // Check if current value is international
  const isInternational = React.useMemo(() => {
    return (field.value || '').toString().startsWith('+')
  }, [field.value])

  // Store value appropriately based on format
  const cleanedValue = React.useMemo(() => {
    const val = (field.value || '').toString()
    if (val.startsWith('+')) {
      // For international: keep +, digits, and spaces
      return val.replace(/[^\d+\s]/g, '')
    }
    // For US: only digits
    return val.replace(/\D/g, '')
  }, [field.value])

  const displayValue = React.useMemo(() => {
    return formatPhoneNumber(cleanedValue)
  }, [cleanedValue])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value

    // Check if user is typing an international number
    if (inputValue.startsWith('+')) {
      // For international: allow +, digits, and spaces
      const cleaned = inputValue.replace(/[^\d+\s]/g, '')
      // Limit to reasonable length (e.g., 20 chars for international)
      const limited = cleaned.slice(0, 20)
      void helpers.setValue(limited)
    } else {
      // For US numbers: extract only digits
      const newDigits = inputValue.replace(/\D/g, '')

      // If we already have 10 digits and user is trying to add more, ignore the change
      if (cleanedValue.length >= 10 && newDigits.length > cleanedValue.length) {
        return // Don't update, we're at max
      }

      // Store only digits in Formik (up to 10 digits for US)
      const limitedDigits = newDigits.slice(0, 10)
      void helpers.setValue(limitedDigits)
    }
  }

  return (
    <FieldWrapper $fullWidth={fullWidth}>
      {label && (
        <FieldLabel htmlFor={`phone-input-${name}`}>
          {label}
          {required && <RequiredIndicator aria-hidden="true"> *</RequiredIndicator>}
        </FieldLabel>
      )}
      <Input
        id={`phone-input-${name}`}
        name={name}
        value={displayValue}
        onChange={handleChange}
        onBlur={field.onBlur}
        error={hasError}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        data-testid="phone-input"
        maxLength={isInternational ? 20 : 14}
        aria-describedby={errorText ? `phone-input-${name}-error` : undefined}
      />
      {errorText && (
        <FieldHelper id={`phone-input-${name}-error`} $error role="alert">
          {errorText}
        </FieldHelper>
      )}
    </FieldWrapper>
  )
}
